import {
  Activity,
  BadgeDollarSign,
  BrainCircuit,
  Check,
  ChevronRight,
  CircleDot,
  Cpu,
  FileSearch,
  ListChecks,
  MessageSquareText,
  Send,
  ShieldCheck,
  Sparkles,
  UsersRound
} from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { formatDate, formatMoney } from "../lib/format";

const prompts = [
  { label: "推进判断", question: "今天最值得推进哪件事？", icon: ListChecks },
  { label: "阻塞扫描", question: "哪些订单或客户已经停滞？", icon: UsersRound },
  { label: "能力变化", question: "最近哪些能力正在形成优势？", icon: Activity },
  { label: "财务推演", question: "我的现金现状与计划有冲突吗？", icon: BadgeDollarSign }
];

const scopeLabels: Record<string, string> = {
  overview: "概览计数", tasks: "待办事项", projects: "项目", workbench: "业务工作台",
  customers: "客户跟进", orders: "订单", finance: "财务现状与计划",
  growth: "成长证据", skills: "技能", events: "相关事实", priorities: "待办与到期业务"
};

export function AiPage() {
  const { data, openModal } = useApp();
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState("");
  const [llmAnswer, setLlmAnswer] = useState<string | null>(null);
  const [thinking, setThinking] = useState(false);
  const [answerMode, setAnswerMode] = useState<"cloud" | "local" | null>(null);
  const [cloudError, setCloudError] = useState<string | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const pending = useRef(false);
  if (!data) return null;

  const activeTasks = data.tasks.filter(
    (task) => !["COMPLETED", "CANCELLED", "ARCHIVED"].includes(task.status)
  ).length;
  const activeOrders = data.customerOrders.filter(
    (order) => !["COMPLETED", "CANCELLED"].includes(order.status)
  ).length;
  const skillEvidence = data.skills.reduce(
    (sum, skill) => sum + Number(skill.evidence_count || 0),
    0
  );
  const factDomains = [
    { label: "待推进事项", value: activeTasks, icon: ListChecks },
    { label: "进行中订单", value: activeOrders, icon: UsersRound },
    { label: "财务现状", value: data.assets.length, icon: BadgeDollarSign },
    { label: "技能证据", value: skillEvidence, icon: Activity }
  ];

  const answer = useMemo(() => {
    if (!asked) return null;
    let today: string;
    try {
      today = new Intl.DateTimeFormat("en-CA", { timeZone: data.profile?.timezone || "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    } catch {
      today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    }
    const topTask = data.tasks.find(
      (task) => !["COMPLETED", "CANCELLED", "ARCHIVED"].includes(task.status)
    );
    const blocked = data.tasks.filter((task) => task.status === "BLOCKED");
    const topSkill = [...data.skills].sort((a, b) => Number(b.total_xp) - Number(a.total_xp))[0];
    if (asked.includes("财务") || asked.includes("资产") || asked.includes("现金")) {
      const assets = data.assetTotals
        .map((item) => formatMoney(Number(item.total_minor), item.currency))
        .join("、");
      const activePlans = data.financialPlans.filter((item) => item.status === "ACTIVE");
      const plans = activePlans
        .map((item) => `${item.plan_type === "SAVING" ? "存钱" : "开销"}「${item.title}」${formatMoney(item.amount_minor, item.currency)}`)
        .join("、");
      return data.assets.length || data.financialPlans.length
        ? {
            title: "财务判断已基于当前现状与财务计划",
            body: `当前财务现状：${assets || "尚未盘点"}；正在进行的计划：${plans || "尚未设置"}。计划只表达未来安排，不会被误算成已经发生的收入或支出。`,
            refs: [
              ...data.assets.slice(0, 2).map((item) => ({ title: item.name, date: item.as_of_date })),
              ...activePlans.slice(0, 2).map((item) => ({ title: item.title, date: item.target_date }))
            ]
          }
        : {
            title: "还没有足够财务事实",
            body: "请先在财务与资产页登记一项当前金额，或写下一项存钱、开销计划。",
            refs: []
          };
    }
    if (asked.includes("订单") || asked.includes("报价") || asked.includes("生意") || asked.includes("客户推进")) {
      const activeOrders = data.customerOrders.filter(
        (order) => !["COMPLETED", "CANCELLED"].includes(order.status)
      );
      const overdueOrders = activeOrders.filter(
        (order) => order.next_followup && order.next_followup < today
      );
      const dueToday = activeOrders.filter(
        (order) => order.next_followup && order.next_followup.slice(0, 10) === today
      );
      if (data.customerOrders.length) {
        const urgent = [...overdueOrders, ...dueToday].slice(0, 3);
        return {
          title: `当前有 ${activeOrders.length} 个订单正在推进`,
          body: overdueOrders.length
            ? `${overdueOrders.length} 个订单已逾期未跟进${urgent.length ? `：${urgent.map((order) => `${order.contact_name}（${order.title}）`).join("、")}` : ""}。建议今天先处理逾期项，再安排新报价。`
            : dueToday.length
              ? `今天有 ${dueToday.length} 个订单到期跟进：${dueToday.map((order) => order.contact_name).join("、")}。`
              : "没有逾期或到期的跟进。保持节奏，给客户发送报价或确认下一步。",
          refs: activeOrders.slice(0, 4).map((order) => ({ title: `${order.contact_name} · ${order.title}`, date: order.next_followup }))
        };
      }
      return {
        title: "还没有订单数据",
        body: "先登记第一个客户和订单，帝王引擎才能帮你盯住跟进节奏、报价和回款。",
        refs: []
      };
    }
    if (asked.includes("关系") || asked.includes("联系人") || asked.includes("跟进")) {
      const due = data.contacts.filter((item) => item.next_followup && item.next_followup.slice(0, 10) <= today);
      return data.contacts.length
        ? {
            title: `当前记录了 ${data.contacts.length} 位真实联系人`,
            body: due.length
              ? `${due.slice(0, 3).map((item) => item.name).join("、")} 已到跟进时间。建议核对下一步行动，并在实际完成后记录成果。`
              : "当前没有已到期的跟进。建议为重要关系设置下一次具体行动，并把事项关联到联系人。",
            refs: data.contacts.slice(0, 4).map((item) => ({ title: item.name, date: item.next_followup }))
          }
        : {
            title: "还没有关系数据",
            body: "先添加真实联系人，再创建关联事项；实际完成后可沉淀为时间线中的成果。",
            refs: []
          };
    }
    if (asked.includes("停滞") || asked.includes("阻塞")) {
      return blocked.length
        ? {
            title: "当前有明确标记的阻塞事项",
            body: `${blocked.slice(0, 3).map((task) => task.title).join("、")}。建议先确认阻塞原因与外部依赖，再决定拆分、等待或取消。`,
            refs: blocked.slice(0, 3).map((task) => ({ title: task.title, date: task.due_date }))
          }
        : {
            title: "目前没有足够证据判断停滞",
            body: "任务中没有明确标记为“受阻”的事项。可以补充问题事件或更新任务状态，再重新分析。",
            refs: []
          };
    }
    if (asked.includes("能力") || asked.includes("增长")) {
      return topSkill && Number(topSkill.evidence_count) > 0
        ? {
            title: `${topSkill.title}拥有当前最多的成长证据`,
            body: `该技能累计 ${topSkill.total_xp} XP，来自 ${topSkill.evidence_count} 条真实事件。经验并不等同于升级，还需要代表性成果和复杂度证据。`,
            refs: [{ title: topSkill.title, date: topSkill.last_used_at }]
          }
        : {
            title: "还不能判断增长最快的能力",
            body: "当前技能证据不足。完成关联技能的任务或记录应用成果后，系统才能比较变化。",
            refs: []
          };
    }
    const businessTask = data.workbench?.topItems[0];
    if (businessTask && (!topTask || asked.includes("工作台") || asked.includes("业务"))) {
      return {
        title: `业务工作台待推进「${businessTask.title}」`,
        body: `工作台当前有 ${data.workbench?.open ?? 0} 项未完成事项，其中 ${data.workbench?.overdue ?? 0} 项逾期。${businessTask.nextAction ? `下一步：${businessTask.nextAction}。` : "请先明确下一步行动。"}这是工作台内的排序建议，并非跨领域价值判断。`,
        refs: [{ title: businessTask.title, date: businessTask.remindAt === null ? null : new Date(businessTask.remindAt).toISOString() }]
      };
    }
    return topTask
      ? {
          title: `建议先推进「${topTask.title}」`,
          body: `它在当前任务池中的解释性排序最高：${topTask.explanations.join("、")}。这是一条建议，你可以替换、拆分或取消。`,
          refs: [{ title: topTask.title, date: topTask.due_date }]
        }
      : {
          title: "当前没有足够任务事实给出优先建议",
          body: "先创建一个与目标或项目关联的真实任务，系统会依据价值、紧迫度、阻塞影响和你的置顶选择排序。",
          refs: []
        };
  }, [asked, data]);

  const runQuestion = async (rawQuestion: string) => {
    const trimmed = rawQuestion.trim();
    if (!trimmed || pending.current) return;
    pending.current = true;
    setQuestion(trimmed);
    setAsked(trimmed);
    setLlmAnswer(null);
    setAnswerMode(null);
    setCloudError(null);
    setScopes([]);
    setThinking(true);
    try {
      const result = await api.aiCoach(trimmed);
      setLlmAnswer(result.answer);
      setScopes(result.scopes ?? []);
      setAnswerMode("cloud");
    } catch (error) {
      setLlmAnswer(null);
      setCloudError(error instanceof Error ? error.message : "云端推理未完成，请稍后重试");
      setAnswerMode("local");
    } finally {
      pending.current = false;
      setThinking(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void runQuestion(question);
  };

  return (
    <div className="page page--ai ai-page engine-page">
      <header className="engine-header">
        <div className="engine-heading">
          <span className="engine-heading__mark"><Cpu size={20} /></span>
          <div>
            <h1>帝王引擎</h1>
            <p>基于真实事实的私人决策系统</p>
          </div>
        </div>
        <div className="engine-core-status" data-online={data.meta?.aiAvailable ? "true" : "false"}>
          <span><CircleDot size={15} /></span>
          <div>
            <strong>{data.meta?.aiAvailable ? "DeepSeek 推理核心已连接" : "本地规则核心待命"}</strong>
            <small>{data.meta?.aiAvailable ? "云端推理 · 按问题最少取数" : "本地规则 · 不调用云端模型"}</small>
          </div>
        </div>
      </header>

      <main className="engine-console">
        <aside className="engine-facts" aria-label="已接入事实域">
          <div className="engine-section-title">
            <FileSearch size={17} />
            <div>
              <h2>事实域</h2>
              <p>可用资料概览，非每次全部发送</p>
            </div>
          </div>
          <div className="engine-fact-list">
            {factDomains.map((domain) => {
              const Icon = domain.icon;
              return (
                <div className="engine-fact" key={domain.label}>
                  <span><Icon size={16} /></span>
                  <div><strong>{domain.value}</strong><small>{domain.label}</small></div>
                  <i aria-hidden="true" />
                </div>
              );
            })}
          </div>
          <div className="engine-facts__boundary">
            <ShieldCheck size={16} />
            <span>只读分析，不自动改动任何记录。</span>
          </div>
        </aside>

        <section className="engine-workspace" aria-label="决策推演区">
          <div className="engine-workspace__head">
            <div>
              <span>当前推演</span>
              <h2>今天要判断什么？</h2>
            </div>
            <span className="engine-readonly"><Check size={13} /> 由你确认</span>
          </div>

          <div className="engine-stage" aria-live="polite">
            {thinking ? (
              <div className="engine-thinking" role="status">
                <span className="engine-thinking__core"><BrainCircuit size={24} /></span>
                <div>
                  <strong>正在调取相关事实</strong>
                  <p>只选取与问题相关的必要资料，云端最长等待 30 秒。</p>
                </div>
                <span className="engine-thinking__signal" aria-hidden="true"><i /><i /><i /></span>
              </div>
            ) : !llmAnswer && !answer ? (
              <div className="engine-prompts">
                <div className="engine-prompts__intro">
                  <MessageSquareText size={18} />
                  <p>直接描述问题，或从四条决策路径开始。</p>
                </div>
                <div className="engine-prompt-list">
                  {prompts.map((prompt) => {
                    const Icon = prompt.icon;
                    return (
                      <button key={prompt.question} type="button" onClick={() => void runQuestion(prompt.question)}>
                        <span><Icon size={17} /></span>
                        <div><small>{prompt.label}</small><strong>{prompt.question}</strong></div>
                        <ChevronRight size={16} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : llmAnswer ? (
              <article className="engine-result">
                <div className="engine-result__label"><Sparkles size={15} /> 帝王引擎 · 事实推演</div>
                <h3>{asked}</h3>
                <p className="engine-result__body">{llmAnswer}</p>
                <div className="engine-result__boundary">
                  <ShieldCheck size={16} />
                  <span>本次提供的资料范围：{scopes.length ? scopes.map((scope) => scopeLabels[scope] ?? scope).join("、") : "概览计数（无详细业务资料）"}。仅作建议，不会执行写入。</span>
                </div>
              </article>
            ) : answer ? (
              <article className="engine-result">
                <div className="engine-result__label"><BrainCircuit size={15} /> 本地规则参考 · 非云端推理</div>
                {cloudError ? <p role="alert" className="engine-result__boundary">{cloudError}。以下仅按本地规则整理，不代表云端推理结果。你可以重新发送问题。</p> : null}
                <h3>{answer.title}</h3>
                <p className="engine-result__body">{answer.body}</p>
                <div className="engine-result__reasoning">
                  <strong><ShieldCheck size={15} /> 判断边界</strong>
                  <span>只使用当前事项、事件、技能兴趣、关系和财务数据；未读取外部信息，也没有执行任何写操作。</span>
                </div>
                <div className="engine-result__sources">
                  <strong><FileSearch size={15} /> 事实依据</strong>
                  {answer.refs.length ? (
                    answer.refs.map((ref, index) => (
                      <span key={`${ref.title}-${index}`}><b>{ref.title}</b><small>{ref.date ? formatDate(ref.date) : "当前记录"}</small></span>
                    ))
                  ) : (
                    <span className="engine-result__insufficient">没有足够可引用事实</span>
                  )}
                </div>
              </article>
            ) : null}
          </div>

          <form className="engine-composer" onSubmit={submit} data-mode={answerMode ?? "idle"}>
            <label htmlFor="engine-question">输入你的判断问题</label>
            <div>
              <input
                id="engine-question"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder="例如：这周先推进哪位客户？"
                autoComplete="off"
              />
              <button type="submit" disabled={!question.trim() || thinking} aria-label={thinking ? "正在推演" : "开始推演"}>
                <Send size={18} />
                <span>{thinking ? "推演中" : "开始推演"}</span>
              </button>
            </div>
            <small>涉及发送、金额、删除与发布的操作仍需你亲自确认。</small>
          </form>
        </section>

        <aside className="engine-guardian" aria-label="事实心核">
          <div className="engine-guardian__visual" aria-hidden="true">
            <div className="engine-guardian__ring" />
            <img src="/emperor-engine-heart-v1.png" width="1024" height="1536" alt="" draggable={false} />
            <span>CORE 01</span>
          </div>
          <div className="engine-guardian__copy">
            <span>事实心核</span>
            <h2>忠于记录，不替你决定。</h2>
            <p>数据不足就明确说不知道；建议只负责照亮选择，最终决定权始终在你。</p>
          </div>
          <ul className="engine-guardrails">
            <li><Check size={14} /> 不自动发送消息</li>
            <li><Check size={14} /> 不修改金额或删除记录</li>
            <li><Check size={14} /> 不虚构缺失的事实</li>
          </ul>
          <button className="engine-record" type="button" onClick={() => openModal("quick")}>
            <Sparkles size={16} />
            <span>补充一条事实</span>
          </button>
        </aside>
      </main>
    </div>
  );
}
